import { motion } from "framer-motion";
import { BarChart3, CalendarRange, Download, FileText, ShieldCheck, Wallet } from "lucide-react";

import { Card, CardHeader, Row } from "@/components/Card";
import Button from "@/components/Button";
import { useApp } from "@/App";
import type { Role } from "@/data/mock";

const ROLE_COPY: Record<Role, { title: string; subtitle: string }> = {
  director: { title: "School reports", subtitle: "A clean starting point for weekly leadership reviews." },
  bursar: { title: "Finance reports", subtitle: "Reconcile collections, balances, and payment channels." },
  teacher: { title: "Teaching reports", subtitle: "Turn classroom activity into a useful weekly summary." },
  student: { title: "My reports", subtitle: "Your progress, attendance, and term milestones." },
  parent: { title: "Family reports", subtitle: "A simple snapshot of your children's term." },
};

const REPORTS = [
  { title: "Term performance", subtitle: "Scores by stream and subject", leading: BarChart3, tone: "blue" as const, status: "Ready" },
  { title: "Attendance register", subtitle: "Daily and term attendance trends", leading: CalendarRange, tone: "green" as const, status: "Ready" },
  { title: "Fee collection", subtitle: "Paid, pending, and outstanding balances", leading: Wallet, tone: "orange" as const, status: "Ready" },
  { title: "Safeguarding summary", subtitle: "Sensitive incidents and follow-ups", leading: ShieldCheck, tone: "red" as const, status: "Private" },
];

export default function Reports() {
  const role = useApp((s) => s.role) ?? "director";
  const copy = ROLE_COPY[role];
  const visibleReports = role === "student" ? REPORTS.slice(0, 2) : role === "parent" ? REPORTS.slice(0, 3) : REPORTS;

  return (
    <div className="px-5 pb-8 pt-5">
      <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <p className="text-ios-footnote text-ios-secondary">Reporting centre · Term 2, 2026</p>
        <h1 className="mt-0.5 text-ios-title-1 font-bold text-ios-label">{copy.title}</h1>
        <p className="mt-1 max-w-sm text-ios-caption-1 text-ios-secondary">{copy.subtitle}</p>
      </motion.div>

      <Card className="mt-5" index={0}>
        <CardHeader title="Quick export" subtitle="Create a snapshot for your next review" />
        <div className="flex gap-2 px-4 pb-4">
          <Button size="md" fullWidth leadingIcon={FileText}>Preview</Button>
          <Button size="md" variant="secondary" fullWidth leadingIcon={Download}>Export</Button>
        </div>
      </Card>

      <Card className="mt-5" index={1}>
        <CardHeader title="Available reports" subtitle={`${visibleReports.length} reports for your role`} />
        <div>
          {visibleReports.map((report, index) => (
            <Row key={report.title} title={report.title} subtitle={report.subtitle} leading={report.leading} leadingTone={report.tone} trailing={<span className="text-ios-caption-1 font-semibold">{report.status}</span>} chevron separator={index < visibleReports.length - 1} />
          ))}
        </div>
      </Card>

      <p className="mt-4 text-center text-ios-caption-2 text-ios-secondary">Reports are generated from the current school workspace.</p>
    </div>
  );
}
